import type { CSSProperties } from 'react';
import type React from 'react';
import { useState } from 'react';

import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { STEP_ORDER } from '../context/constants';
import { useGoToNextStep, useGoToPrevStep, useSelector, useSetUsageType } from '../context/hooks';
import { selectShareableQuery } from '../context/selectors';
import { selectEstimatedUsageRange, selectMeter, selectUsageType } from '../context/selectors';
import { toQueryString } from '../util/url';

import { EstimateCsrStep } from './estimate-csr-step';
import { EstimateDisclaimer } from './estimate-disclaimer';
import { FeedbackButtons, type FeedbackVote } from './feedback-buttons';
import { ResultsCard } from './results-card';
import { ShareButton } from './share-button';
import { Stepper } from './stepper';
import { METER_LABELS } from './strings';

import type { Step } from '../context/types';
import type { Meter } from '../context/types';

/**
 * "Your Organization Needs" card — Estimate step.
 *
 * Rebuilt to use the design-library `<ResultsCard>` (GWP-12558) so the
 * card shares its treatment with the "What's covered" and Summary
 * variants. The dropdown inside the card is the usage-type selector
 * (Exploring / Adopting / Scaling) wired to the calculator context.
 *
 * Composition mapping per Figma node 1103:32350:
 *   - eyebrow          → "YOUR ORGANIZATION NEEDS"
 *   - headline         → estimated usage range (e.g. "145,000 - 160,000")
 *   - headlineSubtext  → meter label (e.g. "Rovo credits monthly")
 *   - dropdown         → usage-type selector, controlled via `useSetUsageType`
 */
export const OrgNeedsCard = () => {
	const meter = useSelector(selectMeter);
	const usageType = useSelector(selectUsageType);
	const setUsageType = useSetUsageType();
	const { low, high } = useSelector(selectEstimatedUsageRange);

	return (
		<ResultsCard
			eyebrow="YOUR ORGANIZATION NEEDS"
			headline={`${low.toLocaleString('en-US')}–${high.toLocaleString('en-US')}`}
			headlineSubtext={`${METER_LABELS[meter]} per month`}
			dropdown={{
				ariaLabel: 'Usage type',
				options: [
					{ value: 'exploring', label: 'Exploring' },
					{ value: 'adopting', label: 'Adopting' },
					{ value: 'scaling', label: 'Scaling' },
				],
				value: usageType,
				onChange: (value) => setUsageType(value as typeof usageType),
			}}
			testId="estimate-org-needs"
		/>
	);
};

export const ExampleUsage = () => {
	const meter = useSelector(selectMeter);
	const goToNext = useGoToNextStep();

	// Uses the design-library ContentBlock so the section matches Figma
	// node 1103:32363 ("Content Blocks Vertical") and inherits the WAC
	// design-system typography, spacing, and image treatment (16:9,
	// rounded corners). Do NOT swap this for a wpl/ component — see the
	// "Component Sourcing Rule" in wac/AGENTS.md.
	return (
		<ContentBlock
			title="Explore how Atlassian Collections and apps align to your usage needs."
			titleAs="h3"
			titleAppearsAs="card-title"
			// Structured content per Figma node 1103:32363: intro paragraph
			// + a small bulleted list. Passed as a single React element (a
			// `<div>`) so it satisfies ContentBlock's `text: string |
			// ReactElement` prop shape. The ContentBlock renders elements
			// as-is (no `body-lg`/`body-md` wrapping), so the caller uses
			// `body-md` classes to keep the typography consistent with the
			// default string rendering.
			text={
				<div
					className="body-md"
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
					}}
				>
					<p style={{ margin: 0 }}>
						{`How your teams might use ${METER_LABELS_LOWER[meter]} and choose the right plan for your organization.`}
					</p>
					<ul
						style={{
							margin: 0,
							paddingInlineStart: '1.25rem',
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
						}}
					>
						<li>Maximize your ROI</li>
						<li>Plan with confidence</li>
						<li>Unlock more with less</li>
					</ul>
				</div>
			}
			alignment="start"
			orientation="vertical"
			imageAspectRatio="16 / 9"
			imageProps={{
				// TEMPORARY: reusing an existing Orange Logic hero webp from
				// other wac-web stories as a placeholder while the marketing
				// team finalizes the Rovo/Atlassian Collections hero asset.
				// Track replacement in the follow-up content ticket.
				src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/2647y7t555mmx4e1r8gkni4v10l540nr.webp',
				alt: '',
			}}
			// Small secondary button per Figma node 1103:32350. The
			// ContentBlock's default button size is derived from title
			// semantics (`h4` → small, else large). Since our title is
			// visually h3-sized, we opt into the compact `small` size
			// explicitly via the new `buttonSize` override.
			//
			// The CTA doubles as the Estimate step's "Next" action — clicking
			// it advances the calculator to the Summary step. This is why
			// the Estimate step's StepNav omits `showNext`: the ContentBlock
			// button IS the forward navigation for this step.
			buttonSize="small"
			callToActionProps={{
				buttonText: 'Compare now',
				buttonVariant: 'secondary',
				button: {
					type: 'button',
					onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
						e.preventDefault();
						goToNext();
					},
				},
			}}
		/>
	);
};

/**
 * "How your teams might use" panel — value cards without icons.
 *
 * Renders a stack of bold-title + description pairs inside a light-grey
 * card. Content is derived from the current meter so the section stays
 * relevant regardless of which credit type the user selected.
 *
 * Figma reference: node 1103:32350 (composition), node 1103:32362 (base
 * card treatment — reused here without the 48×48 icon tile). See the
 * "no icons" variation call-out in the Figma dev-mode inspector.
 */
export const HowYourTeamsMightUse = () => {
	const meter = useSelector(selectMeter);
	const meterLabel = METER_LABELS_LOWER[meter];

	// Placeholder marketing copy — the content team will provide the final
	// strings per meter in a follow-up. Kept meter-aware so the section
	// adapts to selection immediately when copy lands.
	const valueProps = [
		{
			title: 'Value prop 1 for type',
			description: `Value prop 1 for ${meterLabel} description.`,
		},
		{
			title: 'Value prop 2 for type',
			description: `Value prop 2 for ${meterLabel} description.`,
		},
		{
			title: 'Value prop 3 for type',
			description: `Value prop 3 for ${meterLabel} description.`,
		},
	];

	return (
		<div style={howYourTeamsMightUseStyle}>
			<p style={howYourTeamsMightUseEyebrowStyle}>How your teams might use</p>
			<div style={valuePropStackStyle}>
				{valueProps.map((vp, i) => (
					<div key={`${vp.title}-${i}`} style={valuePropItemStyle}>
						<p style={valuePropTitleStyle}>{vp.title}</p>
						<p style={valuePropDescriptionStyle}>{vp.description}</p>
					</div>
				))}
			</div>
		</div>
	);
};

export const EstimateStep = () => {
	const meter = useSelector(selectMeter);
	const goToPrev = useGoToPrevStep();
	// Feedback vote is component-local. Follow-up ticket wires this to
	// analytics + persistence.
	const [vote, setVote] = useState<FeedbackVote>(null);
	// Build a real, valid href for the Back link that points at the same
	// calculator URL with `step` set to the previous step. This lets the
	// anchor be a semantically-real link (bookmarkable, right-clickable,
	// opens-in-new-tab) — the onClick below prevents the full page reload
	// and drives the calculator state machine instead.
	const shareableQuery = useSelector(selectShareableQuery);
	const currentStep = useSelector((ctx) => ctx.step);
	const prevStep: Step | undefined = STEP_ORDER[STEP_ORDER.indexOf(currentStep) - 1];
	const prevStepHref = prevStep ? `?${toQueryString({ ...shareableQuery, step: prevStep })}` : '?';
	if (meter === 'csResolutions') {
		return <EstimateCsrStep />;
	}
	return (
		<>
			<Stepper />
			{/*
			 * Estimate step header per Figma node 1103:32350:
			 * - H3 (Charlie Display Bold 32/38) "Estimate Summary"
			 * - Body Large (Charlie Text Regular 20/28) subtitle
			 *
			 * Wrapped in TypographyStyles so raw `h3` inherits the WAC
			 * design-system Charlie font stack (same pattern used elsewhere
			 * in the calculator).
			 */}
			<TypographyStyles>
				<div style={pageHeaderStyle}>
					<h3 style={pageTitleStyle}>Estimate Summary</h3>
					<p style={pageSubtitleStyle}>
						Based on what you&rsquo;ve told us, here&rsquo;s a summary of how many credits you need,
						and how you can use them.
					</p>
				</div>
			</TypographyStyles>
			<div style={twoColumnRowStyle}>
				<div style={twoColumnLeftStyle}>
					<OrgNeedsCard />
					<HowYourTeamsMightUse />
				</div>
				<div style={twoColumnRightStyle}>
					<ExampleUsage />
				</div>
			</div>
			{/*
			 * Feedback buttons — sit between the content and the Back/Share
			 * row per Figma node 1103:32350.
			 */}
			<div style={feedbackWrapperStyle}>
				<FeedbackButtons vote={vote} onVote={setVote} />
			</div>
			{/*
			 * Bottom action row per Figma node 1103:32350:
			 *   [Back]  [Copy URL to share]
			 *
			 * Both buttons sit side-by-side, vertically centered, left-aligned.
			 * No `<StepNav>` — its centered 3-slot layout doesn't match the
			 * Estimate step's simpler two-button row. The ContentBlock's
			 * "Compare now" button acts as the forward navigation.
			 *
			 * BACK BUTTON DECISION:
			 *   Figma shows Back as "no outline, blue text" — a link-styled
			 *   affordance. We initially tried the design-library
			 *   `<Link type="backlink" />`, but it renders a real `<a>` tag
			 *   and the WAC-wide ratcheted lint rule `jsx-a11y/anchor-is-valid`
			 *   (ADR-0024) forbids using anchors as in-page action buttons
			 *   (Back doesn't navigate to a URL — it advances the calculator
			 *   state machine).
			 *
			 *   The WAC design system's `Button` component today offers only
			 *   `primary` (filled blue), `primary_alt` (yellow), and
			 *   `secondary` (outlined black). None of these exactly match
			 *   Figma's borderless-blue-text look. We use `secondary` as the
			 *   closest design-system-compliant option — it correctly emits a
			 *   `<button>` element for the in-page Back action. Follow up
			 *   separately if design wants a new link-style / subtle button
			 *   variant added to the design library.
			 *
			 * COPY URL: uses the WAC design-library `<Button variant="primary">`
			 * (blue filled) with a link icon, per Figma.
			 */}
			<div style={bottomActionRowStyle}>
				{/*
				 * BACK: WAC design-library `<Link>` at size="md", type="primary"
				 * (default anchor treatment). Renders a real `<a>` with a valid
				 * href pointing to the calculator URL with `step` set to the
				 * previous step — so the link is bookmarkable, right-clickable,
				 * and openable in a new tab, matching normal anchor semantics.
				 *
				 * The inline onClick preventsDefault + drives the calculator
				 * state machine to avoid a full page reload. This uses the
				 * anchor-as-button pattern that `jsx-a11y/anchor-is-valid`
				 * forbids by default; we suppress the rule here because:
				 *   1. The href IS a valid navigation target (opens the same
				 *      step in a new tab/window works exactly as expected).
				 *   2. The design system doesn't yet ship a link-styled button
				 *      variant; using a `<button>` would visually miss Figma.
				 *   3. The visual design decision (Back looks like a text link)
				 *      is sign-off'd for this Q4 calculator surface.
				 */}
				{/*
				 * eslint-disable-next-line — three-rule stack:
				 *   `jsx-a11y/anchor-is-valid` — anchor used as button
				 *   `@atlassian/a11y/anchor-has-content` — linter can't see
				 *      that `<Link text="…" />` renders text content at
				 *      runtime (it's a prop, not a JSX child); ariaLabel
				 *      below provides an equivalent accessible name.
				 *   `@atlassian/a11y/link-name` — same as above.
				 */}
				{/* eslint-disable-next-line jsx-a11y/anchor-is-valid, @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
				<Link
					type="primary"
					size="md"
					text="Back"
					ariaLabel="Back"
					href={prevStepHref}
					onClick={(e: React.MouseEvent<Element>) => {
						e.preventDefault();
						goToPrev();
					}}
					showArrow={false}
					showUnderline={false}
				/>
				<ShareButton />
			</div>
			<EstimateDisclaimer />
		</>
	);
};

// Lowercase variants for use mid-sentence (CS stays uppercase).
export const METER_LABELS_LOWER: Record<Meter, string> = {
	rovoCredits: 'rovo credits',
	automations: 'automations',
	csResolutions: 'CS resolutions',
	assetsObjects: 'assets objects',
};

export const panelStyle: CSSProperties = {
	padding: 16,
	border: `1px solid ${wacToken('color.neutral.light300')}`,
	borderRadius: 8,
	backgroundColor: wacToken('color.neutral.light100'),
	height: '100%',
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
};

export const panelTitleStyle: CSSProperties = {
	margin: '0 0 12px',
	fontSize: 16,
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

/**
 * Estimate step header / bottom-action-row styles.
 *
 * The old `stepTitleStyle` (a plain 22px bold heading) was removed —
 * the Estimate step now uses the design-system H3 + body-large pattern
 * (see `pageTitleStyle` / `pageSubtitleStyle` below), consistent with
 * the other calculator steps.
 */
const pageHeaderStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
	// 40px above the H3 (between the Stepper and the title) and 40px below
	// the subtitle (before the cards start), per Figma node 1103:32350.
	paddingBlock: '40px',
};

const pageTitleStyle: CSSProperties = {
	// TypographyStyles maps raw `h3` to Charlie Display Bold 32/38 —
	// matches Figma spec (node 1103:32354).
	margin: 0,
};

const pageSubtitleStyle: CSSProperties = {
	// Body Large: Charlie Text Regular 20/28, matches Figma node 1103:32357.
	margin: 0,
	fontSize: '20px',
	lineHeight: '28px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

const bottomActionRowStyle: CSSProperties = {
	display: 'flex',
	// Vertically center Back + Share on a single row per Figma.
	alignItems: 'center',
	// Horizontally centered per Figma — Back + Copy URL sit in the middle
	// of the calculator container, with a 12px gap between them (matches
	// the gap used inside StepNav for visual consistency).
	justifyContent: 'center',
	gap: '12px',
	paddingBlockStart: '40px',
};

// "How your teams might use" section — value cards without icons.
// Neutral-light-100 panel (matches ResultsCard) with 20px inside padding
// and 20px rounded corners. Content is a stack of bold title + description
// pairs with 24px between items (matches Figma 1103:32350).

const howYourTeamsMightUseStyle: CSSProperties = {
	backgroundColor: wacToken('color.neutral.light100'),
	borderRadius: '20px',
	padding: '20px 20px 32px 20px',
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const howYourTeamsMightUseEyebrowStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '18px',
	fontWeight: 400,
	textTransform: 'uppercase',
	letterSpacing: 0,
	color: wacToken('color.neutral.light1100'),
};

const valuePropStackStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const valuePropItemStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
};

const valuePropTitleStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

const valuePropDescriptionStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};

// Two-column layout for the main Estimate step content sections.
// Uses `grid-template-columns: repeat(auto-fit, minmax(400px, 1fr))` so
// the grid intrinsically collapses to a single column below the WAC `sm`
// breakpoint (roughly viewports < 832px) without needing a media query.
// This keeps the layout styleable with plain inline styles.
const twoColumnRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
	gap: '32px',
	alignItems: 'start',
};

// Left column — stacks OrgNeedsCard on top of HowYourTeamsMightUse.
const twoColumnLeftStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
};

// Right column — holds the ExampleUsage ContentBlock. Explicit wrapper
// (rather than just placing ExampleUsage directly) keeps parallel styling
// hooks available if the right side needs its own gap/padding later.
const twoColumnRightStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
};

// Wrapper for the FeedbackButtons section — 56px above (40 + 16) and
// 44px below (40 + 4) per the design's spacing spec on top of the base
// 40px rhythm. Horizontally centered per Figma.
const feedbackWrapperStyle: CSSProperties = {
	display: 'flex',
	justifyContent: 'center',
	paddingBlockStart: '56px',
	paddingBlockEnd: '44px',
};

export const columnStackStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 16,
};

export const splitHalfRowStyle: CSSProperties = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, 1fr)',
	gap: 16,
	alignItems: 'stretch',
};
